import React, { createContext, useContext, useMemo } from "react";

import type { SimScenario, SimWallet } from "./types";

/**
 * Context shape exposed to widgets and their orchestrators.
 *
 * `realHooks` is the injection point for `apps/main` to wire its
 * real orchestration hooks (useSwap, useStakeTransaction, …) into
 * the resolver. External consumers (docs site, marketing site)
 * leave it undefined; they only render widgets in sim scenarios,
 * so the real branch is never taken.
 *
 * Note on stability: an orchestrator picks its hook implementation
 * by reading this context value at *render* time. To avoid breaking
 * the rules of hooks, the resolver must always return the same hook
 * for a given (scenario, realHooks) pair across the widget's
 * lifetime. The provider's `useMemo` below ensures the value
 * identity is stable when its inputs are stable.
 */
export interface SimModeContextValue {
    enabled: boolean;
    scenario: SimScenario;
    wallet?: SimWallet;
    /**
     * Pause (ms) after a sim transaction settles before it replays.
     * Forwarded to each widget's `useSimulatedTransaction` so the
     * auto-played BuyWidget/TradeWidget sims loop. `-1` plays once and
     * stops. Defaults to 5000.
     */
    repeatPauseMs?: number;
    /**
     * Real orchestration hooks injected at the app root. Sim
     * orchestrators (defined per-widget in
     * `sim/orchestrators/*.ts`) call into these when `enabled` is
     * false. Shape is intentionally untyped here — each orchestrator
     * declares the exact field it expects. Type-safety is preserved
     * by the orchestrator extracting the field via the
     * `SimRealHooks` interface that orchestrators augment.
     */
    realHooks?: SimRealHooks;
}

/**
 * Augmentable interface for real-hook injection. Each orchestrator
 * extends this declaration via TypeScript module augmentation so the
 * shape stays type-safe without circular imports.
 *
 * Example (in `useSwapOrchestration.ts`):
 *
 *     declare module "@solomon-apps/shared/sim" {
 *         interface SimRealHooks {
 *             useSwap?: typeof import(".../useSwap").useSwap;
 *         }
 *     }
 */
export interface SimRealHooks {}

const DEFAULT_VALUE: SimModeContextValue = {
    enabled: false,
    scenario: "off"
};

const SimModeContext = createContext<SimModeContextValue>(DEFAULT_VALUE);

export interface SimModeProviderProps {
    /**
     * Scenario to play. `"off"` (or omitting the provider entirely)
     * makes orchestrators fall through to the real hook.
     */
    scenario?: SimScenario;
    /** Fake wallet identity to inject. Required if scenario != "off". */
    wallet?: SimWallet;
    /**
     * Pause (ms) after a sim transaction settles before it replays the
     * auto-played schedule. Defaults to 5000. Set to `-1` to play once
     * and stop.
     */
    repeatPauseMs?: number;
    /**
     * App-side wiring of real orchestration hooks. Only set this in
     * `apps/main`; external consumers leave it undefined.
     */
    realHooks?: SimRealHooks;
    children: React.ReactNode;
}

/**
 * Mounts sim-mode context for the subtree.
 *
 * Real app behavior is unchanged when this provider is *not*
 * present — the default context value disables sim and orchestrators
 * call real hooks directly.
 *
 * Real app behavior is also unchanged when this provider IS present
 * with `scenario="off"` — same fall-through behavior. The provider
 * is safe to leave mounted permanently with a runtime scenario
 * switch. Switching between live and simulated mode remounts the subtree
 * so the selected implementation starts with a fresh hook scope. Injected
 * hook functions must stay stable for the provider lifetime.
 *
 * @example external Vite consumer
 * ```tsx
 * import { SimModeProvider } from "@solomon-apps/shared/sim";
 * import { BuyWidget } from "@solomon-apps/shared/widgets";
 *
 * <SimModeProvider scenario="happy" wallet={{ connected: true, address: "11…" }}>
 *   <BuyWidget />
 * </SimModeProvider>
 * ```
 */
export function SimModeProvider({
    scenario = "off",
    wallet,
    repeatPauseMs = 5000,
    realHooks,
    children
}: SimModeProviderProps): React.ReactElement {
    const value = useMemo<SimModeContextValue>(
        () => ({
            enabled: scenario !== "off",
            scenario,
            wallet,
            repeatPauseMs,
            realHooks
        }),
        [scenario, wallet, repeatPauseMs, realHooks]
    );
    return (
        <SimModeContext.Provider
            key={`${value.enabled ? "sim" : "live"}:${realHooks ? "injected" : "standalone"}`}
            value={value}
        >
            {children}
        </SimModeContext.Provider>
    );
}

/**
 * Read the current sim-mode context. Returns the default
 * `{enabled: false, scenario: "off"}` when no provider is mounted —
 * orchestrators that branch on `enabled` therefore call the real
 * hook by default.
 */
export function useSimMode(): SimModeContextValue {
    return useContext(SimModeContext);
}
